import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ResponseTier } from '@/types/api.types';

type UserAction = 'Pending' | 'Accepted' | 'Modified' | 'Rejected';
type SortOption = 'index' | 'confidence-desc' | 'confidence-asc' | 'tier';

interface FilterState {
  // Tier filters
  selectedTiers: ResponseTier[];

  // Status filters
  selectedStatuses: UserAction[];

  // Confidence filter
  minConfidence: number;
  maxConfidence: number;

  // Search
  searchQuery: string;

  // Sort
  sortBy: SortOption;

  // Actions
  toggleTier: (tier: ResponseTier) => void;
  setSelectedTiers: (tiers: ResponseTier[]) => void;
  toggleStatus: (status: UserAction) => void;
  setSelectedStatuses: (statuses: UserAction[]) => void;
  setConfidenceRange: (min: number, max: number) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: SortOption) => void;
  resetFilters: () => void;
}

const defaultFilters = {
  selectedTiers: [] as ResponseTier[],
  selectedStatuses: [] as UserAction[],
  minConfidence: 0,
  maxConfidence: 100,
  searchQuery: '',
  sortBy: 'index' as SortOption,
};

export const useFilterStore = create<FilterState>()(
  persist(
    (set) => ({
      ...defaultFilters,

      toggleTier: (tier) =>
        set((state) => ({
          selectedTiers: state.selectedTiers.includes(tier)
            ? state.selectedTiers.filter((t) => t !== tier)
            : [...state.selectedTiers, tier],
        })),

      setSelectedTiers: (selectedTiers) => set({ selectedTiers }),

      toggleStatus: (status) =>
        set((state) => ({
          selectedStatuses: state.selectedStatuses.includes(status)
            ? state.selectedStatuses.filter((s) => s !== status)
            : [...state.selectedStatuses, status],
        })),

      setSelectedStatuses: (selectedStatuses) => set({ selectedStatuses }),

      setConfidenceRange: (minConfidence, maxConfidence) =>
        set({ minConfidence, maxConfidence }),

      setSearchQuery: (searchQuery) => set({ searchQuery }),

      setSortBy: (sortBy) => set({ sortBy }),

      resetFilters: () => set(defaultFilters),
    }),
    {
      name: 'insight-filter-store',
      // Only persist the sort preference. Tier/status/search/confidence are
      // per-task UI state — persisting them caused a previous session's
      // "Reviewed" toggle to hydrate on the next review page load and hide
      // pending questions until the user manually clicked "All".
      partialize: (state) => ({ sortBy: state.sortBy }),
    }
  )
);
